import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  HStack,
  Text,
  FormLabel,
  Input,
  Button,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation, Trans } from 'react-i18next';

import { trackEvent } from '$/logger';
import { useProjectModal } from '$/pages/DashboardPages/pages/Projects/hooks/useProjectModal';
import {
  createProjectFolder,
  deleteProject,
  deleteProjectFolder,
  renameProject,
  renameProjectFolder,
} from '$/services/usecases/projects';
import { projectFoldersQuery } from '$/services/usecases/projects/queries';
import { useProjectStore } from '$/stores/useProjectStore';

type ModalInfo = {
  header: string;
  body?: ReactNode;
  footer?: ReactNode;
};

export const ProjectModal = () => {
  const { t } = useTranslation();
  const isOpen = useProjectModal.useIsOpen();
  const modalMode = useProjectModal.useModalMode();
  const onClose = useProjectModal.useOnClose();
  const project = useProjectModal.useProject();
  const projectFolder = useProjectModal.useProjectFolder();
  const updateProject = useProjectStore.useUpdateProject();
  const [nameInputValue, setNameInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { refetch: refetchProjectFolders } = useQuery(
    projectFoldersQuery({ enabled: false }),
  );

  const oldName = project?.name ?? projectFolder?.name ?? '';
  const isRenameDisabled =
    !nameInputValue || nameInputValue === oldName || isLoading;
  const isCreateDisabled = !nameInputValue || isLoading;

  useEffect(() => {
    if (project) {
      setNameInputValue(project.name);
    } else if (projectFolder) {
      setNameInputValue(projectFolder.name);
    } else {
      setNameInputValue('');
    }
  }, [project, isOpen, projectFolder]);

  const onRenameProject = async () => {
    if (!project) return;
    setIsLoading(true);
    const response = await renameProject(project.id, nameInputValue);
    if (response.isSuccessful) {
      updateProject({ ...project, name: nameInputValue });
      onClose();
    }
    setIsLoading(false);
  };

  const onRenameProjectFolder = async () => {
    if (!projectFolder) return;
    setIsLoading(true);
    const response = await renameProjectFolder(
      projectFolder.id,
      nameInputValue,
    );
    if (response.isSuccessful) {
      void refetchProjectFolders();
      onClose();
    }
    setIsLoading(false);
  };

  const onDeleteProjectFolder = async () => {
    if (!projectFolder) return;
    setIsLoading(true);
    const response = await deleteProjectFolder(projectFolder.id);
    if (response.isSuccessful) {
      void refetchProjectFolders();
      onClose();
    }
    setIsLoading(false);
  };

  const onDeleteProject = async () => {
    if (!project) return;
    setIsLoading(true);
    const response = await deleteProject(project.id);
    if (response.isSuccessful) {
      useProjectStore.setState((state) => ({
        projects: state.projects.filter((p) => p.id !== project.id),
      }));
      onClose();
    }
    setIsLoading(false);
  };

  const onCreateProjectFolder = async () => {
    trackEvent('Projects', 'NewFolder');
    if (!nameInputValue) return;
    setIsLoading(true);
    const response = await createProjectFolder(nameInputValue);
    if (response.isSuccessful) {
      void refetchProjectFolders();
      onClose();
    }
    setNameInputValue('');
    setIsLoading(false);
  };

  const { header, body, footer }: ModalInfo = (() => {
    switch (modalMode) {
      case 'renameProject':
        return {
          header: t('dashboard.projects.renameProjectModal.header'),
          body: (
            <>
              <FormLabel fontSize='sm'>
                {t('dashboard.projects.renameProjectModal.projectName')}
              </FormLabel>
              <Input
                onChange={(event) => setNameInputValue(event.target.value)}
                onKeyDown={async (event) => {
                  if (event.key === 'Enter' && !isRenameDisabled) {
                    await onRenameProject();
                  }
                }}
                value={nameInputValue}
              />
            </>
          ),
          footer: (
            <HStack justify='space-between' w='full'>
              <Button
                fontSize='sm'
                onClick={() => {
                  setNameInputValue('');
                  onClose();
                }}
                variant='text'
              >
                {t('dashboard.projects.renameProjectModal.cancelButton')}
              </Button>
              <Button
                fontSize='sm'
                isDisabled={isRenameDisabled}
                isLoading={isLoading}
                onClick={onRenameProject}
              >
                {t('dashboard.projects.renameProjectModal.saveButton')}
              </Button>
            </HStack>
          ),
        };
      case 'deleteProject':
        return {
          header: t('dashboard.projects.deleteProjectModal.header'),
          body: (
            <Text>
              {
                <Trans
                  i18nKey='dashboard.projects.deleteProjectModal.deleteInformation'
                  values={{
                    projectName: project?.name,
                  }}
                  shouldUnescape
                />
              }
            </Text>
          ),
          footer: (
            <HStack justify='space-between' w='full'>
              <Button
                fontSize='sm'
                onClick={() => {
                  setNameInputValue('');
                  onClose();
                }}
                variant='text'
              >
                {t('dashboard.projects.deleteProjectModal.cancelButton')}
              </Button>
              <Button
                fontSize='sm'
                isLoading={isLoading}
                onClick={onDeleteProject}
                variant='danger'
              >
                {t('dashboard.projects.deleteProjectModal.deleteButton')}
              </Button>
            </HStack>
          ),
        };
      case 'createProjectFolder':
        return {
          header: t('dashboard.projects.createProjectFolderModal.header'),
          body: (
            <>
              <FormLabel
                fontSize='sm'
                data-testid='project-folder-name-input-label'
              >
                {t(
                  'dashboard.projects.createProjectFolderModal.projectFolderName',
                )}
              </FormLabel>
              <Input
                onChange={(event) => setNameInputValue(event.target.value)}
                onKeyDown={async (event) => {
                  if (event.key === 'Enter' && !isCreateDisabled) {
                    await onCreateProjectFolder();
                  }
                }}
                placeholder={t('dashboard.authentication.defaultPlaceholder', {
                  field: t(
                    'dashboard.projects.createProjectFolderModal.projectFolderName',
                  ),
                })}
                value={nameInputValue}
              />
            </>
          ),
          footer: (
            <HStack justify='space-between' w='full'>
              <Button
                fontSize='sm'
                onClick={() => {
                  setNameInputValue('');
                  onClose();
                }}
                variant='text'
              >
                {t('dashboard.projects.createProjectFolderModal.cancelButton')}
              </Button>
              <Button
                fontSize='sm'
                data-testid='create-project-folder-button'
                isDisabled={isCreateDisabled}
                isLoading={isLoading}
                onClick={onCreateProjectFolder}
              >
                {t('dashboard.projects.createProjectFolderModal.createButton')}
              </Button>
            </HStack>
          ),
        };
      case 'editProjectFolder':
        return {
          header: t('dashboard.projects.editProjectFolderModal.header'),
          body: (
            <>
              <FormLabel fontSize='sm'>
                {t(
                  'dashboard.projects.createProjectFolderModal.projectFolderName',
                )}
              </FormLabel>
              <Input
                onChange={(event) => setNameInputValue(event.target.value)}
                onKeyDown={async (event) => {
                  if (event.key === 'Enter' && !isRenameDisabled) {
                    await onRenameProjectFolder();
                  }
                }}
                value={nameInputValue}
              />
            </>
          ),
          footer: (
            <HStack justify='space-between' w='full'>
              <Button
                fontSize='sm'
                onClick={() => {
                  setNameInputValue('');
                  onClose();
                }}
                variant='text'
              >
                {t('general.cancel')}
              </Button>
              <Button
                fontSize='sm'
                data-testid='rename-project-folder-button'
                isDisabled={isRenameDisabled}
                isLoading={isLoading}
                onClick={onRenameProjectFolder}
              >
                {t('dashboard.projects.editProjectFolderModal.renameButton')}
              </Button>
            </HStack>
          ),
        };
      case 'deleteProjectFolder':
        return {
          header: t('dashboard.projects.deleteProjectFolderModal.header'),
          body: (
            <Text>
              {
                <Trans
                  i18nKey='dashboard.projects.deleteProjectFolderModal.deleteInformation'
                  values={{
                    projectFolderName: projectFolder?.name,
                  }}
                  shouldUnescape
                />
              }
            </Text>
          ),
          footer: (
            <HStack justify='space-between' w='full'>
              <Button
                fontSize='sm'
                onClick={() => {
                  setNameInputValue('');
                  onClose();
                }}
                variant='text'
              >
                {t('general.cancel')}
              </Button>
              <Button
                fontSize='sm'
                isLoading={isLoading}
                onClick={onDeleteProjectFolder}
                variant='danger'
              >
                {t('dashboard.projects.deleteProjectFolderModal.deleteButton')}
              </Button>
            </HStack>
          ),
        };
      default:
        return {
          header: '',
          body: <></>,
          footer: <></>,
        };
    }
  })();

  return (
    <Modal
      isCentered
      isOpen={isOpen}
      onClose={() => {
        setNameInputValue('');
        onClose();
      }}
    >
      <ModalOverlay />
      <ModalContent
        gap='4'
        w='full'
        maxW='498px'
        p='10'
        bg='background'
        borderRadius='12px'
      >
        <ModalHeader p='0' fontSize='h7'>
          <Text>{header}</Text>
          <ModalCloseButton mt='6' mr='6' />
        </ModalHeader>
        <ModalBody mb='6' p='0' fontSize='sm'>
          {body}
        </ModalBody>
        <ModalFooter p='0'>
          <HStack justifyContent='space-between' w='full'>
            {footer}
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
